import {
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@tanstack/react-router';
import { Button, buttonVariants } from '@/components/ui/button';
import { useTranslator } from '@/hooks/useTranslator';
import { useAddressMissingBanner } from './hooks/useAddressMissingBanner';

/**
 * Non-blocking reminder that the company's own address is incomplete.
 *
 * Country, city, zip code and address line 1 are required for every company;
 * this nudges the members who can fill them in. "Remind me later" snoozes it
 * for a day — it keeps coming back until the address is complete.
 */
export function AddressMissingBanner() {
  const { getTranslation: t } = useTranslator({ page: 'addressBanner' });
  const { visible, companyId, missingFields, handleDismiss } =
    useAddressMissingBanner();

  if (!visible) return null;

  const fields = missingFields
    .map(field => t(`fields.${field}`))
    .join(t('separator'));

  return (
    <div
      role='status'
      className='flex w-full shrink-0 items-center gap-2 border-b border-warning/40 bg-warning/15 px-4 py-2'
    >
      <FontAwesomeIcon
        icon={faTriangleExclamation}
        className='text-sm text-warning'
      />
      <span className='flex-1 truncate text-xs font-medium text-foreground'>
        {t('message', { fields })}
      </span>
      <Link
        to='/network/$companyId/general-info'
        params={{ companyId }}
        search={{ genTemplate: 'ABOUT' }}
        className={buttonVariants()}
      >
        {t('action')}
      </Link>
      <Button
        variant='ghost'
        size='icon'
        aria-label={t('dismiss')}
        title={t('dismiss')}
        onClick={handleDismiss}
      >
        <FontAwesomeIcon icon={faXmark} />
      </Button>
    </div>
  );
}
